import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { ProgramsAPI } from '@business/api/programs';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { ProgramId, ProgramResponse, CompactProgramsResponse } from '@business/entities/programs';
import { ProgramsFilterData } from '@modules/programs/ProgramsPage/ProgramsPage';

export class ProgramsBloc extends BaseBloc {
  private readonly programsRepo = new Repository<ProgramResponse | CompactProgramsResponse>({
    getItemId: item => item.id,
  });

  readonly programs = this.programsRepo.items;
  readonly allPrograms$ = this.programsRepo.items$;
  readonly selectProgram = this.programsRepo.selectItem;

  onReset() {
    this.programsRepo.reset();
  }

  fetchPrograms = (filter?: ProgramsFilterData, fullResponse?: boolean) => {
    return ProgramsAPI.fetchPrograms(filter, fullResponse).pipe(
      this.programsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_programs_error') }),
    );
  };

  fetchProgram = (id: ProgramId) => {
    return ProgramsAPI.fetchProgram(id).pipe(
      this.programsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_programs_error') }),
    );
  };

  createProgram = (data: Partial<ProgramResponse>) => {
    return ProgramsAPI.createPrograms(data).pipe(
      this.programsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_programs'),
      }),
    );
  };

  updateProgram = (id: ProgramId, data: Partial<ProgramResponse>) => {
    return ProgramsAPI.updatePrograms(id, data).pipe(
      this.programsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_programs'),
      }),
    );
  };

  deleteProgram = (id: ProgramId) => {
    return showDeleteConfirmation('Delete Program', 'Do you really want to remove this Program?').pipe(
      switchMap(() => ProgramsAPI.deletePrograms(id)),
      this.programsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_programs'),
      }),
    );
  };

  archiveProgram = (id: ProgramId) => {
    return showDeleteConfirmation('Archive Program', 'Do you really want to archive this Program?').pipe(
      switchMap(() => ProgramsAPI.archiveProgram(id)),
      this.programsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('archive_programs'),
      }),
    );
  };

  changeProgramDate = (id: ProgramId, date: number) => {
    return ProgramsAPI.updateProgramDate(id, date).pipe(
      this.programsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_programs'),
      }),
    );
  };

  changeProgramStatus = (id: ProgramId, status: string) => {
    return ProgramsAPI.updateProgramStatus(id, status).pipe(
      this.programsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_programs'),
      }),
    );
  };
}
